import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { AccountFaq, AccountFaqCategory } from '../../models/faq';
import { ModalService } from '../../services/modal.service';

/**
 * Dedicated editor for a single account FAQ — opened from
 * AccountFaqListComponent. Question + answer + the /faq hub category.
 *
 * Which PAGES an FAQ appears on is not set here (#202): a page names the FAQs
 * it wants in its own Page FAQs block, so the relationship is edited where the
 * page is composed rather than from both ends.
 *
 * Save shape mirrors the package editor: the mutated AccountFaq is returned
 * via dialogRef.close({saved, faq}) and the list component persists it via
 * the RBAC content route.
 */
@Component({
  selector: 'app-account-faq',
  templateUrl: './account-faq.component.html',
  styleUrls: ['./account-faq.component.scss'],
})
export class AccountFaqComponent implements OnInit {
  faq!: AccountFaq;
  isNew = false;
  /** True while the save request is in flight — Save spins, Cancel locks. */
  saving = false;

  form!: FormGroup;

  /** Category records, passed in by the list (#202). */
  categoryOptions: AccountFaqCategory[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.faq = this.config.data.faq;
    this.isNew = !!this.config.data.isNew;
    this.form = this.fb.group({
      question: new FormControl(this.faq.question ?? '', [Validators.required]),
      answer: new FormControl(this.faq.answer ?? '', [Validators.required]),
      order: new FormControl(this.faq.order ?? null),
      category: new FormControl(this.faq.category ?? ''),
      // Absent means shown: every FAQ predating this toggle was on the hub by
      // virtue of having a category, so defaulting to false would empty the
      // page the moment this ships.
      onFaqPage: new FormControl(this.faq.onFaqPage !== false),
    });

    this.categoryOptions = Array.isArray(this.config.data.categoryOptions)
      ? this.config.data.categoryOptions
      : [];

  }


  onCancel(): void {
    this.ref.close({ saved: false });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;
    this.faq.question = (value.question ?? '').trim();
    this.faq.answer = (value.answer ?? '').trim();
    // `pages` is no longer edited here (#202) — a page names the FAQs it wants
    // via its Page FAQs block. Whatever is stored is passed through untouched
    // rather than cleared: the site ignores it, and wiping a field on every
    // save of an unrelated edit is not this dialog's business.

    const category = (value.category ?? '').trim();
    if (category) {
      this.faq.category = category;
    } else {
      delete this.faq.category;
    }

    this.faq.onFaqPage = value.onFaqPage !== false;

    if (typeof value.order === 'number') {
      this.faq.order = value.order;
    } else {
      delete this.faq.order;
    }

    // Save-then-close: the dialog stays open (Save spinning) until the server
    // confirms, so a publish cannot race an in-flight write; on rejection it
    // stays open with everything typed rather than losing the edit.
    const save = this.config.data.save as ((v: any) => any) | undefined;
    if (!save) {
      this.ref.close({ saved: true, faq: this.faq });
      return;
    }
    this.saving = true;
    save(this.faq).subscribe({
      next: () => this.ref.close({ saved: true, faq: this.faq, persisted: true }),
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }
}
